import React from 'react';

import useOutsideClick from '@hero/ui-kit/hooks/useOutsideClick';
import MenuDotsIcon from '../../icons/MenuDotsIcon';
import ClickableOpacity from '@hero/ui-kit/inputs/ClickableOpacity';
import P from '@hero/ui-kit/typography/P';
import * as Style from './style.module.scss';

interface DropdownItem {
    label: string;
    onClick: () => void;
}

interface SplitButtonProps {
    label: string;
    onClick: () => void;
    dropdownItems: DropdownItem[];
    disabled?: boolean;
    active?: boolean;
    locked?: boolean;
}

const SplitButton: React.FC<SplitButtonProps> = ({
    label,
    onClick,
    dropdownItems,
    disabled = false,
    active = false,
    locked = false
}) => {
    const [isOpen, setIsOpen] = React.useState(false);
    const [ref, isOutsideClick] = useOutsideClick(isOpen);

    React.useEffect(() => {
        if (isOutsideClick) {
            setIsOpen(false);
        }
    }, [isOutsideClick]);

    const handleCaretClick = React.useCallback(() => {
        setIsOpen((prev) => !prev);
    }, []);

    const handleItemClick = React.useCallback((itemOnClick: () => void) => {
        itemOnClick();
        setIsOpen(false);
    }, []);

    return (
        <div
            className={`${Style.splitButton}${active ? ` ${Style.active}` : ''}${locked ? ` ${Style.locked}` : ''}`}
            ref={ref as React.RefObject<HTMLDivElement>}
        >
            <button
                className={Style.splitButtonMain}
                onClick={onClick}
                disabled={disabled}
                type="button"
            >
                {label}
            </button>
            <button
                className={`${Style.splitButtonCaret}${disabled ? ` ${Style.disabled}` : ''}`}
                onClick={handleCaretClick}
                disabled={disabled}
                type="button"
                aria-label={`${label} options`}
            >
                <MenuDotsIcon size="small" />
            </button>
            {isOpen && (
                <div className={Style.splitButtonDropdown}>
                    {dropdownItems.map((item) => (
                        <ClickableOpacity
                            key={item.label}
                            className={Style.splitButtonDropdownItem}
                            onClick={() => handleItemClick(item.onClick)}
                            alt={item.label}
                            fullWidth
                        >
                            <P noDefaultMargin size="small">
                                {item.label}
                            </P>
                        </ClickableOpacity>
                    ))}
                </div>
            )}
        </div>
    );
};

export default SplitButton;
